import { Controller } from '@hotwired/stimulus'

/**
 * Read-more controller with accessibility enhancements
 * - Updates aria-expanded dynamically
 * - Manages focus properly
 * - Provides keyboard support
 * - Screen reader announcements
 * - Hides its own toggle when the text already fits uncut
 */
export default class extends Controller {
  static targets = ["content", "button"]
  static values = {
    moreText: { type: String, default: "Read more" },
    lessText: { type: String, default: "Read less" },
    lines: { type: Number, default: 2 },
    tolerance: { type: Number, default: 1 }
  }

  connect() {
    this.open = false

    // Set initial state
    this.clampTo(this.linesValue)

    // Ensure button has proper initial ARIA attributes
    if (this.hasButtonTarget) {
      this.buttonTarget.textContent = this.moreTextValue
      this.buttonTarget.setAttribute('aria-expanded', 'false')
      
      // Generate unique ID for content if not present
      if (!this.contentTarget.id) {
        this.contentTarget.id = `read-more-content-${Math.random().toString(36).substr(2, 9)}`
      }
      
      // Link button to content
      this.buttonTarget.setAttribute('aria-controls', this.contentTarget.id)
    }

    // How many lines a given text runs to depends on the width it is laid out
    // in, so whether the clamp bites is not something the server can decide up
    // front — the same description is two lines beside a wide product gallery
    // and five on a phone. Observing the element rather than listening for
    // window resize, because its width also changes without one, and observe()
    // fires immediately so this covers the first measurement too.
    //
    // Width only: syncButton changes the element's HEIGHT, which would feed
    // straight back into an observer watching both and loop.
    this.observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width)
      if (width === this.lastWidth) return

      this.lastWidth = width
      this.syncButton()
    })
    this.observer.observe(this.contentTarget)

    // Webfonts swap in after first paint and shift the metrics with them,
    // which does not change the clamped box and so never reaches the observer.
    document.fonts?.ready?.then(() => this.syncButton())
  }

  disconnect() {
    this.observer?.disconnect()
  }

  toggle(event) {
    event.preventDefault()
    
    this.open = !this.open
    const button = this.buttonTarget

    if (this.open) {
      // Expanded state
      button.textContent = this.lessTextValue
      button.setAttribute('aria-expanded', 'true')
      this.clampTo(999)

      // Announce to screen readers
      this.announceChange('Description expanded')
    } else {
      // Collapsed state
      button.textContent = this.moreTextValue
      button.setAttribute('aria-expanded', 'false')
      this.clampTo(this.linesValue)

      // Announce to screen readers
      this.announceChange('Description collapsed')
    }

    // Keep focus on button for keyboard users
    button.focus()
    this.syncButton()
  }

  /**
   * Decide whether collapsing this text earns its keep, and drop the clamp
   * entirely when it does not.
   *
   * The toggle sits on its own line, so a clamp that hides ONE line saves no
   * vertical space at all — it just trades the last line of the sentence for a
   * "Read more", which is how you end up with a control that reveals a single
   * orphaned word. `tolerance` is how many hidden lines are not worth a button:
   * at the default 1, text has to overrun by two or more lines before it gets
   * clamped, and anything shorter simply renders in full.
   *
   * Measured from a known collapsed state, because scrollHeight and
   * clientHeight agree once the clamp is off and the decision would stick.
   */
  syncButton() {
    if (!this.hasButtonTarget || this.open) return

    const content = this.contentTarget
    this.clampTo(this.linesValue)

    // Sub-pixel line-height rounding reports a fraction of a line of overflow
    // on text that does fit, hence the 1px on top of the line allowance.
    const lineHeight = parseFloat(getComputedStyle(content).lineHeight) || 0
    const allowance = lineHeight * this.toleranceValue + 1
    const worthClamping = content.scrollHeight - content.clientHeight > allowance

    if (!worthClamping) this.clampTo(999)
    this.buttonTarget.hidden = !worthClamping
  }

  clampTo(lines) {
    this.contentTarget.style.setProperty('--read-more-line-clamp', String(lines))
  }

  /**
   * Announce state changes to screen readers using a live region
   */
  announceChange(message) {
    // Create or reuse live region
    let liveRegion = document.getElementById('read-more-announcer')
    
    if (!liveRegion) {
      liveRegion = document.createElement('div')
      liveRegion.id = 'read-more-announcer'
      liveRegion.className = 'visually-hidden'
      liveRegion.setAttribute('aria-live', 'polite')
      liveRegion.setAttribute('aria-atomic', 'true')
      document.body.appendChild(liveRegion)
    }
    
    // Clear and set new message
    liveRegion.textContent = ''
    setTimeout(() => {
      liveRegion.textContent = message
    }, 100)
  }
}
